/**
 * GraphQL queries and mutations for the Integrations page (ShelterLuv).
 */

import { gql } from '@apollo/client';
import type { Gender, Species } from '../../types/gql-op-types';

/** Fetch the current user's ShelterLuv configuration status. Returns null if not configured. */
export const rescueShelterluvStatusQuery = gql`
  query rescueShelterluvStatus {
    rescueShelterluvStatus {
      enabled
      apiKeyEndsWith
      lastImportAt
      thirdPartyChipIncrementalEnabled
      thirdPartyChipBackfillEnabled
      emailUpdateOptIn
    }
  }
`;

export interface RescueShelterluvStatusData {
  rescueShelterluvStatus: {
    enabled: boolean;
    apiKeyEndsWith: string;
    lastImportAt: string | null;
    thirdPartyChipIncrementalEnabled: boolean;
    thirdPartyChipBackfillEnabled: boolean;
    emailUpdateOptIn: boolean;
  } | null;
}

/** Fetch the current user's reseller info including shelterluv chip stats. */
export const rescueResellerStatsQuery = gql`
  query rescueResellerStats {
    currentUser {
      id
      chipReseller {
        id
        name
        stats {
          chipsRegisteredOnShelterluv
        }
      }
    }
  }
`;

export interface RescueResellerStatsData {
  currentUser: {
    id: string;
    chipReseller: {
      id: string;
      name: string;
      stats: {
        chipsRegisteredOnShelterluv: number;
      };
    } | null;
  };
}

/** Enable ShelterLuv integration with an API key and sync options. */
export const rescueEnableShelterluvMutation = gql`
  mutation rescueEnableShelterluv($input: RescueEnableShelterluvInput!) {
    rescueEnableShelterluv(input: $input) {
      id
      name
      preferences {
        shelterluv {
          enabled
          apiKeyEndsWith
          lastImportAt
          thirdPartyChipBackfillEnabled
          thirdPartyChipIncrementalEnabled
        }
      }
      stats {
        chipsRegisteredOnShelterluv
      }
    }
  }
`;

export interface RescueEnableShelterluvData {
  rescueEnableShelterluv: {
    id: string;
    name: string;
    preferences: {
      shelterluv: {
        enabled: boolean;
        apiKeyEndsWith: string;
        lastImportAt: string | null;
        thirdPartyChipBackfillEnabled: boolean;
        thirdPartyChipIncrementalEnabled: boolean;
      };
    };
    stats: {
      chipsRegisteredOnShelterluv: number;
    };
  };
}

/** Disable ShelterLuv integration. */
export const rescueDisableShelterluvMutation = gql`
  mutation rescueDisableShelterluv {
    rescueDisableShelterluv {
      id
      preferences {
        shelterluv {
          enabled
        }
      }
    }
  }
`;

export interface RescueDisableShelterluvData {
  rescueDisableShelterluv: {
    id: string;
    preferences: {
      shelterluv: {
        enabled: boolean;
      } | null;
    };
  };
}

/**
 * Opt the rescue in or out of email updates for registrations imported from ShelterLuv.
 * Selects the full shelterluv preferences object so the normalized ChipReseller cache
 * entry written by rescueEnableShelterluv is not clobbered with a narrower object.
 */
export const rescueSetShelterluvEmailUpdateOptInMutation = gql`
  mutation rescueSetShelterluvEmailUpdateOptIn($optIn: Boolean!) {
    rescueSetShelterluvEmailUpdateOptIn(optIn: $optIn) {
      id
      preferences {
        shelterluv {
          enabled
          apiKeyEndsWith
          lastImportAt
          thirdPartyChipBackfillEnabled
          thirdPartyChipIncrementalEnabled
          emailUpdateOptIn
        }
      }
    }
  }
`;

export interface RescueSetShelterluvEmailUpdateOptInData {
  rescueSetShelterluvEmailUpdateOptIn: {
    id: string;
    preferences: {
      shelterluv: {
        enabled: boolean;
        apiKeyEndsWith: string;
        lastImportAt: string | null;
        thirdPartyChipBackfillEnabled: boolean;
        thirdPartyChipIncrementalEnabled: boolean;
        emailUpdateOptIn: boolean;
      } | null;
    };
  };
}

// ── Manual CSV import ──────────────────────────

/**
 * Bulk-create chipped pets from a CSV upload. Two-phase semantics on the
 * backend:
 *
 *   Phase 1 — pre-flight validation. Every row is checked against the
 *   batched chip/breed/contact-state snapshot, collecting EVERY
 *   independent failure per row (bad phone + breed not found + chip
 *   belongs to another reseller all surface together). If any row fails
 *   pre-flight, the whole batch is rejected — `validationFailed: true`,
 *   `rows` contains only the failures, NO writes are applied. The rescue
 *   fixes the entire row in one pass and re-uploads.
 *
 *   Phase 2 — per-row apply. Only runs if pre-flight passes for every
 *   row. Each row applies in its own transaction (create or claim the
 *   chip, register the pet, attach the owner). A phase-2 failure means
 *   a race after validation (e.g. another rescue claimed the chip
 *   between phases) — that row reports a single error in `errors[]`
 *   while every other row still commits.
 *
 * `errors` is always present (empty `[]` on success). Owner address
 * fields are all optional. Pets are created in the rescue's RESCUE
 * household; the owner contact is attached via the same
 * `assignNewOwnerToChip` flow used by Add Owner.
 */
export const rescueImportPetsWithChipsMutation = gql`
  mutation rescueImportPetsWithChips($input: RescueImportPetsWithChipsInput!) {
    rescueImportPetsWithChips(input: $input) {
      successCount
      errorCount
      validationFailed
      rows {
        rowIndex
        petId
        chipSerialNumber
        errors
      }
    }
  }
`;

export interface RescueImportPetWithChipRowInput {
  // Required
  name: string;
  chipSerialNumber: string;
  species: Species;
  gender: Gender;
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string;
  ownerPhone: string;
  weight: number;
  yearOfBirth: number;
  monthOfBirth: number;
  // Optional pet attributes
  breed?: string | null;
  dayOfBirth?: number | null;
  color?: string | null;
  // Optional owner address
  ownerStreet?: string | null;
  ownerApt?: string | null;
  ownerCity?: string | null;
  ownerState?: string | null;
  ownerZip?: string | null;
}

export interface RescueImportPetsWithChipsInput {
  pets: RescueImportPetWithChipRowInput[];
}

export interface RescueImportPetsWithChipsRowResult {
  rowIndex: number;
  petId: string | null;
  chipSerialNumber: string | null;
  /**
   * All validation/apply errors for this row, empty on success. Phase 1
   * collects every independent failure per row; phase-2 race failures
   * surface as a single-element array.
   */
  errors: string[];
}

export interface RescueImportPetsWithChipsData {
  rescueImportPetsWithChips: {
    successCount: number;
    errorCount: number;
    /**
     * True when pre-flight rejected the batch — no writes applied,
     * `rows` lists only the failing rows. False on a successful (or
     * partially successful) batch — `rows` then includes every input
     * row with `errors: []` on the ones that committed.
     */
    validationFailed: boolean;
    rows: RescueImportPetsWithChipsRowResult[];
  };
}

// ── Save the raw CSV file after a successful import ───
//
// Persists the exact CSV that was uploaded along with summary metadata
// (file name, size, row counts, ids of pets that were created). Called
// from the import wizard after `rescueImportPetsWithChips` returns
// successfully so the rescue has an audit trail of what was imported and
// when. Failures here should NOT block the import — the pets are already
// created — they're logged and surfaced as a non-fatal warning.

/** Persist a successful CSV import for audit / re-export. */
export const saveRescueImportFileMutation = gql`
  mutation saveRescueImportFile($input: SaveRescueImportFileInput!) {
    saveRescueImportFile(input: $input) {
      id
      fileName
      fileSizeBytes
      rowCount
      successCount
      errorCount
      createdAt
    }
  }
`;

export interface SaveRescueImportFileInput {
  /** Original filename as the user uploaded it (e.g. "spca-polk-april-batch.csv"). */
  fileName: string;
  /** Raw CSV text contents, header row included. The backend stores this as-is. */
  fileContent: string;
  /** Number of data rows in the file (excludes the header). */
  rowCount: number;
  /** Mirror of the `successCount` returned by `rescueImportPetsWithChips`. */
  successCount: number;
  /** Mirror of the `errorCount` returned by `rescueImportPetsWithChips`. */
  errorCount: number;
  /** GraphQL ids of pets created in this batch, in CSV row order. */
  importedPetIds: string[];
}

export interface SavedRescueImportFile {
  id: string;
  fileName: string;
  fileSizeBytes: number;
  rowCount: number;
  successCount: number;
  errorCount: number;
  createdAt: string;
}

export interface SaveRescueImportFileData {
  saveRescueImportFile: SavedRescueImportFile;
}
